/**
 * kl-product.js — 買えるLEON: 旧 Prestige v4 theme.js の商品ページ機能の vanilla JS 移植。
 *
 * 対象セクション: kl-product-template / kl-product-template-subscription /
 *                 kl-product-pre-order-template / kl-product-pre-order-custom-order-template
 *   （[data-section-type="product"] を持つ .kl-legacy 内セクション）
 *
 * 移植元コンポーネント（reference/old-theme/assets/theme.js）:
 *   - ProductVariants（バリアント選択→価格/在庫/SKU/単価/カートボタン更新・history state・Ajaxカート）
 *   - Popover（ドロップダウン型オプションセレクタ）
 *   - VariantSelector（カラーカルーセル選択。スクロール同期/セルクリック/価格・画像更新まで旧準拠）
 *   - ProductSection（ギャラリー: モバイルカルーセル/スタックscrollspy/シェアトグル/クイックナビ/高さ計算）
 *   - Modal / Currency.formatMoney / Image.getSizedImageUrl / Responsive.matchesBreakpoint
 *     （Collapsible と Animation.slideUp・slideDown は kl-custom.js のグローバル実装
 *       window.klCollapsible に統一。本ファイルでは toggle-collapsible をバインドしない）
 *   - ProductImageZoom（PhotoSwipe 4 完全移植。ライブラリは assets/kl-photoswipe.min.js を
 *     各テンプレートが zoom 有効時のみ defer 読込。旧テーマ同様 UIクラスは false で
 *     セクション内の .pswp カスタムマークアップ + kl-legacy.css の pswp スタイルを使う）
 *
 * 依存グローバル: window.klProduct（各セクションのインラインscriptが定義。
 *   旧テーマ theme.liquid の window.theme / window.routes / window.languages 相当）
 *   window.PhotoSwipe（kl-photoswipe.min.js。zoom 有効時のみ）
 *
 * KL-TODO:
 *   - StoreAvailability（店舗受取表示）は未移植（現行設定 show_pickup_availability=false）
 *   - ProductModel / 外部動画の再生UI（plyr・YT.Player・Shopify XR）は未移植。
 *     セル切替時の停止は対応（native video.pause() / YouTube・Vimeo は postMessage）
 *   - Ajaxカート成功時の product:added はv11のカートドロワーと未連携（現行設定 cart_type=page のため未使用）
 *   - SPRレビュー(ProductReviews)は未移植（現行設定 reviews_enabled=false）
 */
(function () {
  'use strict';

  var LAP_MIN = 1008; // 旧テーマ breakpoint: lap-and-up（この幅以上で stack ギャラリーはカルーセル無効）

  /* ------------------------------------------------------------------ */
  /* helpers                                                             */
  /* ------------------------------------------------------------------ */

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList || []);
  }

  function getSiblings(element, selector) {
    var siblings = [];
    var el = element.parentNode ? element.parentNode.firstElementChild : null;
    for (; el; el = el.nextElementSibling) {
      if (el === element) continue;
      if (selector && !el.matches(selector)) continue;
      siblings.push(el);
    }
    return siblings;
  }

  function isLapAndUp() {
    return window.matchMedia('(min-width: ' + LAP_MIN + 'px)').matches;
  }

  /* 旧 helper/Responsive.matchesBreakpoint の移植（本ファイルで使う分のみ） */
  function matchesBreakpoint(breakpoint) {
    switch (breakpoint) {
      case 'phone':
        return window.matchMedia('screen and (max-width: 640px)').matches;
      case 'pocket':
        return window.matchMedia('screen and (max-width: 1007px)').matches;
      case 'lap-and-up':
        return window.matchMedia('screen and (min-width: 1008px)').matches;
      case 'supports-hover':
        return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    }
    return false;
  }

  /* 旧 helper/Image.getSizedImageUrl の移植（Liquid の img_url フィルタ相当のCDN URL生成）。
     旧実装は `_WxH` サフィックス型URLのみ対応だったが、移植版テンプレートは image_url フィルタ
     （`?v=...&width=...` クエリ型URL）を使うため、クエリ型にも対応する（挙動は同一: WxH内にフィット） */
  function getSizedImageUrl(src, size) {
    if (size === null) {
      return src;
    }

    if (size === 'master') {
      return src.replace(/http(s)?:/, '');
    }

    var match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i);

    if (match) {
      var prefix = src.split(match[0]);
      var suffix = match[0];

      return (prefix[0] + '_' + size + suffix).replace(/http(s)?:/, '');
    }

    var dimensions = /^(\d+)x(\d+)$/.exec(size);

    if (dimensions) {
      var parts = src.split('?');
      var params = (parts[1] || '').split('&').filter(function (param) {
        return param !== '' && param.indexOf('width=') !== 0 && param.indexOf('height=') !== 0;
      });
      params.push('width=' + dimensions[1]);
      params.push('height=' + dimensions[2]);
      return (parts[0] + '?' + params.join('&')).replace(/http(s)?:/, '');
    }

    return null;
  }

  /* 旧 helper/Currency.formatMoney の移植 */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || (window.klProduct && window.klProduct.moneyFormat) || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'undefined' ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
    }
    return formatString.replace(placeholderRegex, value);
  }

  function languages() {
    return (window.klProduct && window.klProduct.languages) || {};
  }

  /* html要素のスクロールロック。旧テーマは <html class="no-scroll"> だったが、
     kl-legacy.css のスコープ化で htmlレベルのクラスが効かないため inline style も併用する */
  function lockScroll() {
    document.documentElement.classList.add('no-scroll');
    document.documentElement.style.overflow = 'hidden';
  }

  function unlockScroll() {
    document.documentElement.classList.remove('no-scroll');
    document.documentElement.style.overflow = '';
  }

  /* PageOverlay は旧テーマでは theme.liquid 直下にあった。各セクションの .kl-legacy 内に出力してある */
  function getPageOverlay(sectionElement) {
    var legacyRoot = sectionElement.closest('.kl-legacy') || sectionElement;
    return legacyRoot.querySelector('.PageOverlay');
  }

  /* ------------------------------------------------------------------ */
  /* Modal（旧 theme.js ではページ全体のグローバルプラグイン）
     Collapsible のグローバル実装は kl-custom.js（window.klCollapsible）に統一。
     ここでは toggle-collapsible をバインドしない（二重発火防止）。 */
  /* ------------------------------------------------------------------ */

  var activeModal = null;
  var modalWasLocked = false;
  var modalTransitionListener = null;
  var modalTrapped = false;

  function openModal(modal) {
    if (activeModal || !modal) return;
    activeModal = modal;
    modalWasLocked = document.documentElement.classList.contains('no-scroll');

    /* 旧 theme.js:3316 の Accessibility.clearTrapFocus()（コメントも旧のまま:
       "Needed as the modal can be open on top of a popover"）。
       ポップオーバー（VariantSelector）やドロワーのトラップが張られたままモーダルが開くと
       トラップ同士が競合するため、先に全て無効化する。無効化されたトラップの tabindex は
       それぞれの close() が呼ぶ release() で外れる（旧 removeTrapFocus と同じ） */
    if (window.klFocusTrap && window.klFocusTrap.clear) window.klFocusTrap.clear();

    /* 旧 Modal は visibility のトランジション完了後にフォーカストラップを張っていた
       （旧theme.js:3318-3319 でリスナ登録 → 3366-3375 _onTransitionEnded で trapFocus）。
       .Modal は kl-legacy.css で visibility:hidden→visible を 0.3s でトランジションしており、
       hidden の間は中の要素を focus できないため、このタイミングでないとトラップできない。
       トラップ本体は kl-custom.js の window.klFocusTrap（ドロワー/検索と共用）。
       サイズ表モーダルは [autofocus] を持たないため、旧 Accessibility.trapFocus（旧theme.js:856）
       と同じく「モーダル自身」に focus が当たる（tabindex="-1" はトラップ側が付ける）。
       先頭のフォーカス可能要素を focus すると overflow:auto の .Modal が開いた瞬間に
       スクロールしてしまうため、この差は見た目に直結する。 */
    modalTransitionListener = function (event) {
      if (event.target !== modal || event.propertyName !== 'visibility') return;
      modal.removeEventListener('transitionend', modalTransitionListener);
      modalTransitionListener = null;
      if (activeModal !== modal || !window.klFocusTrap) return;
      /* 旧 theme.js:3372 の trapFocus(this.activeModal, 'modal') 相当（namespace も旧と同じ） */
      window.klFocusTrap.trap(modal, 'modal');
      modalTrapped = true;
    };
    modal.addEventListener('transitionend', modalTransitionListener);

    modal.setAttribute('aria-hidden', 'false');
    lockScroll();
    // Modal--fullScreen 以外はオーバーレイ表示（商品ページのサイズ表モーダルは fullScreen）
    if (!modal.classList.contains('Modal--fullScreen')) {
      var overlay = getPageOverlay(modal);
      if (overlay) {
        overlay.classList.add('is-visible');
        overlay.addEventListener('click', closeModal);
      }
    }
  }

  function closeModal() {
    if (!activeModal) return;

    /* 開くトランジションの途中で閉じられた場合、閉じる側の visibility トランジションで
       リスナが再発火して閉じたモーダルにトラップが張られてしまうため先に解除する */
    if (modalTransitionListener) {
      activeModal.removeEventListener('transitionend', modalTransitionListener);
      modalTransitionListener = null;
    }

    /* 旧 theme.js:3350 の removeTrapFocus 相当。実際にトラップを張れた時だけ解除する
       （張れていない時に release すると、別要素のトラップやフォーカスを奪ってしまう） */
    if (modalTrapped && window.klFocusTrap) window.klFocusTrap.release('modal');
    modalTrapped = false;

    if (!activeModal.classList.contains('Modal--fullScreen')) {
      var overlay = getPageOverlay(activeModal);
      if (overlay) {
        overlay.classList.remove('is-visible');
        overlay.removeEventListener('click', closeModal);
      }
    }
    activeModal.setAttribute('aria-hidden', 'true');
    activeModal = null;
    if (!modalWasLocked) unlockScroll();
  }

  /* 旧 Modal._checkOpenByHash（旧theme.js:3393-3402、呼び出しはコンストラクタの旧theme.js:3280）の移植。
     旧コメント: "Some forms needs to be open inside a modal, and on page reload we must make sure to
     properly open the modal again" ＝ location.hash が .Modal の id と一致すればそのモーダルを開く。 */
  function checkOpenByHash() {
    var hash = window.location.hash,
        modal = document.getElementById(hash.replace('#', ''));

    /* 旧は「.Modal クラスを持つ要素」であれば何でも対象にしていた（旧テーマは Modal がページ全体で
       1実装だったため）。移植版は Modal 実装が kl-product.js（商品セクション）/
       templates/customers/addresses.liquid / sections/kl-password-template.liquid に分かれている。
       kl-product.js を読み込むのは商品セクションの4テンプレートのみで住所・パスワードページには
       読み込まれない（＝実際には競合しない）が、二重に開く事故を確実に避けるため、
       このスクリプトが面倒を見る .kl-legacy 配下の .Modal に限定する。
       商品ページのサイズ表モーダル（snippets/kl-product-form.liquid:337 の
       #modal-{{ size_chart_page.handle }}）は .kl-legacy 内なので旧同様に開く。 */
    if (modal && modal.classList.contains('Modal') && modal.closest('.kl-legacy')) {
      openModal(modal);
    }
  }

  function bindGlobalHandlers() {
    // 他のkl-セクションや将来の共通実装との二重バインドを防ぐ
    if (document.klProductGlobalsBound) return;
    document.klProductGlobalsBound = true;

    document.addEventListener('click', function (event) {
      var openModalButton = event.target.closest('[data-action="open-modal"]');
      if (openModalButton) {
        openModal(document.getElementById(openModalButton.getAttribute('aria-controls')));
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      var closeModalButton = event.target.closest('[data-action="close-modal"]');
      if (closeModalButton) {
        closeModal();
        event.preventDefault();
      }
      /* toggle-collapsible は kl-custom.js のグローバル実装が処理する（ここではバインドしない） */
    });

    document.addEventListener('keyup', function (event) {
      if (event.keyCode === 27) closeModal();
    });

    /* 旧 Modal はコンストラクタで _attachListeners() の直後に _checkOpenByHash() を実行していた
       （旧theme.js:3279-3280）。同じ順序で実行する */
    checkOpenByHash();
  }

  /* ------------------------------------------------------------------ */
  /* Popover（旧 components/Popover の移植、商品オプションのドロップダウン） */
  /* ------------------------------------------------------------------ */

  function Popover(element, options) {
    this.element = element;
    this.activator = options.activator || document.querySelector('[aria-controls="' + element.getAttribute('id') + '"]');
    this.onValueChanged = options.onValueChanged || function () {};
    this.threshold = options.threshold || 20;
    this.isOpen = false;
    this.sectionElement = options.sectionElement || null;
    if (!this.activator) return;
    this._attachListeners();
  }

  Popover.prototype._attachListeners = function () {
    var self = this;
    this._clickOutsideListener = function (event) {
      if (!event.target.closest('.Popover') && !event.target.closest('.Modal') && event.target !== self.activator && !self.activator.contains(event.target)) {
        self.close();
      }
    };
    this._closeListener = this.close.bind(this);

    this.activator.addEventListener('click', function () {
      self.isOpen ? self.close() : self.open();
    });

    this.element.addEventListener('keyup', function (event) {
      if (self.isOpen && event.keyCode === 27) self.close();
    });

    this.element.addEventListener('click', function (event) {
      if (event.target.closest('[data-action="close-popover"]')) {
        self.close();
        return;
      }
      var valueButton = event.target.closest('[data-action="select-value"]');
      if (valueButton) {
        getSiblings(valueButton, '.is-selected').forEach(function (item) {
          item.classList.remove('is-selected');
        });
        valueButton.classList.add('is-selected');
        self.onValueChanged(valueButton.getAttribute('data-value'), valueButton, self.activator);
        self.close();
      }
    });
  };

  Popover.prototype.open = function () {
    if (this.isOpen || this.activator.getAttribute('aria-controls') !== this.element.id) return;
    this.element.setAttribute('aria-hidden', 'false');
    this.activator.setAttribute('aria-expanded', 'true');
    lockScroll();

    if (isLapAndUp()) {
      document.body.addEventListener('click', this._clickOutsideListener);
      this._position();
    } else {
      this.element.removeAttribute('style');
      var overlay = getPageOverlay(this.sectionElement || this.element);
      if (overlay) {
        overlay.classList.add('is-visible');
        overlay.addEventListener('click', this._closeListener);
      }
    }
    this.isOpen = true;
  };

  Popover.prototype.close = function () {
    if (!this.isOpen) return;
    this.element.setAttribute('aria-hidden', 'true');
    this.activator.setAttribute('aria-expanded', 'false');
    unlockScroll();

    if (isLapAndUp()) {
      document.body.removeEventListener('click', this._clickOutsideListener);
    } else {
      var overlay = getPageOverlay(this.sectionElement || this.element);
      if (overlay) {
        overlay.classList.remove('is-visible');
        overlay.removeEventListener('click', this._closeListener);
      }
    }
    this.isOpen = false;
  };

  /* 旧 Popover._position（preferredPosition: 'left'）の移植 */
  Popover.prototype._position = function () {
    var topPosition = 0,
        rightPosition = 0,
        position = 'left',
        alignment = '',
        threshold = this.threshold;

    var windowHeight = window.innerHeight,
        activatorBoundingRect = this.activator.getBoundingClientRect(),
        halfElementHeight = this.element.clientHeight / 2;

    if (activatorBoundingRect.top >= halfElementHeight && windowHeight - activatorBoundingRect.bottom >= halfElementHeight) {
      alignment = 'center';
    } else if (windowHeight - activatorBoundingRect.bottom >= halfElementHeight) {
      alignment = 'bottom';
    } else {
      alignment = 'top';
    }

    rightPosition = window.innerWidth - activatorBoundingRect.left + threshold;
    if (alignment === 'center') {
      topPosition = activatorBoundingRect.top - this.element.clientHeight / 2 + this.activator.clientHeight / 2;
    } else if (alignment === 'top') {
      topPosition = activatorBoundingRect.bottom - this.element.clientHeight;
    } else {
      topPosition = activatorBoundingRect.top;
    }

    ['Popover--positionBottom', 'Popover--positionTop', 'Popover--positionCenter', 'Popover--alignTop', 'Popover--alignCenter', 'Popover--alignBottom'].forEach(function (item) {
      this.element.classList.remove(item);
    }, this);
    this.element.classList.add('Popover--position' + (position.charAt(0).toUpperCase() + position.slice(1)));
    this.element.classList.add('Popover--align' + (alignment.charAt(0).toUpperCase() + alignment.slice(1)));
    this.element.setAttribute('style', 'top: ' + parseInt(topPosition, 10) + 'px; right: ' + parseInt(rightPosition, 10) + 'px;');
  };

  /* ------------------------------------------------------------------ */
  /* VariantSelector（カラーカルーセル。旧 components/VariantSelector の移植）
     Flickityカルーセルは CSS scroll-snap + スクロール位置検知に置換し、
     旧挙動（スクロールでの is-selected 同期 / セルクリック=選択 or 確定 /
     variant:changed での画像・価格更新）を再現。
     現行設定では show_color_carousel=false のため本番では未使用だが、設定ONでも動作する。 */
  /* ------------------------------------------------------------------ */

  function VariantSelector(element, parentVariants, options) {
    this.element = element;
    this.parentVariants = parentVariants;
    this.activator = document.querySelector('[aria-controls="' + element.getAttribute('id') + '"]');
    this.onValueChanged = (options && options.onValueChanged) || function () {};
    this.isOpen = false;
    this.carousel = element.querySelector('.VariantSelector__Carousel');
    this.cells = toArray(element.querySelectorAll('.VariantSelector__Item'));
    this.choices = toArray(element.querySelectorAll('.VariantSelector__Choice'));
    if (!this.activator) return;
    this._attachListeners();
  }

  VariantSelector.prototype._selectedIndex = function () {
    for (var i = 0; i < this.cells.length; i++) {
      if (this.cells[i].classList.contains('is-selected')) return i;
    }
    return 0;
  };

  /* 旧 Flickity の select イベント + _variantChanged 相当:
     セル/チョイスの is-selected を同期する（確定はしない） */
  VariantSelector.prototype._syncSelection = function (index) {
    this.cells.forEach(function (cell, i) {
      cell.classList.toggle('is-selected', i === index);
    });
    this.choices.forEach(function (choice, i) {
      choice.classList.toggle('is-selected', i === index);
    });
  };

  /* 旧 Carousel.selectCell 相当（スクロール移動 + 同期） */
  VariantSelector.prototype._selectIndex = function (index, immediate) {
    this._syncSelection(index);
    if (this.carousel) {
      this.carousel.scrollTo({ left: index * this.carousel.clientWidth, behavior: immediate ? 'auto' : 'smooth' });
    }
  };

  /* 旧 _variantSelected（Flickity staticClick）: 選択中セルのクリックで確定、
     それ以外のセルのクリックはそのセルへ移動 */
  VariantSelector.prototype._onCellClicked = function (cell) {
    var index = this.cells.indexOf(cell);
    if (index === -1) return;

    if (this._selectedIndex() === index) {
      this.onValueChanged(cell.getAttribute('data-option-value'), cell, this.activator);
      this.close();
    } else {
      this._selectIndex(index);
    }
  };

  /* 旧 _onVariantChanged: バリアント確定時に候補画像の表示切替と価格表示を更新 */
  VariantSelector.prototype._onParentVariantChanged = function (event) {
    var self = this;
    var selectedVariant = event.detail.variant;

    if (!selectedVariant) return;

    toArray(this.element.querySelectorAll('.VariantSelector__ImageWrapper')).forEach(function (image) {
      var colorPosition = parseInt(image.parentElement.getAttribute('data-option-position')) - 1,
          combinationExcludedColor = '';

      selectedVariant['options'].forEach(function (optionValue, index) {
        if (index !== colorPosition) {
          combinationExcludedColor += optionValue;
        }
      });

      if (image.getAttribute('data-variant-title') === combinationExcludedColor) {
        image.setAttribute('aria-hidden', 'false');
      } else {
        image.setAttribute('aria-hidden', 'true');
      }
    });

    var foundColorIndex = 0;

    toArray(this.element.querySelectorAll('.VariantSelector__ChoicePrice')).forEach(function (priceElement, index) {
      var colorPosition = parseInt(priceElement.getAttribute('data-color-position')) - 1;

      self.parentVariants.productData['variants'].forEach(function (variant) {
        var matchOptionsExcludedColor = true;

        variant['options'].forEach(function (option, optionIndex) {
          if (optionIndex !== colorPosition) {
            if (variant['options'][optionIndex] !== selectedVariant['options'][optionIndex]) {
              matchOptionsExcludedColor = false;
            }
          }
        });

        if (matchOptionsExcludedColor && variant['options'][colorPosition] === selectedVariant['options'][colorPosition]) {
          if (foundColorIndex++ === index) {
            priceElement.innerHTML = '<span class="Heading Text--subdued">' + formatMoney(variant['price']) + '</span>';
          }
        }
      });
    });
  };

  VariantSelector.prototype._attachListeners = function () {
    var self = this;
    this._closeListener = this.close.bind(this);

    this.activator.addEventListener('click', function () {
      self.isOpen ? self.close() : self.open();
    });

    this.element.addEventListener('keyup', function (event) {
      if (self.isOpen && event.keyCode === 27) self.close();
    });

    this.element.addEventListener('click', function (event) {
      var cell = event.target.closest('.VariantSelector__Item');
      if (cell) {
        self._onCellClicked(cell);
        return;
      }
      var choice = event.target.closest('.VariantSelector__Choice');
      if (choice) {
        self._selectIndex(self.choices.indexOf(choice));
        return;
      }
      var selectButton = event.target.closest('[data-action="select-variant"]');
      if (selectButton) {
        var selectedCell = self.cells[self._selectedIndex()];
        self.onValueChanged(selectedCell.getAttribute('data-option-value'), selectedCell, self.activator);
        self.close();
      }
    });

    // 旧 Flickity select イベント相当: スクロール位置からアクティブセルを検出して同期
    if (this.carousel) {
      var ticking = false;
      this.carousel.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          var width = self.carousel.clientWidth;
          if (width <= 0) return;
          var index = Math.round(self.carousel.scrollLeft / width);
          index = Math.min(Math.max(index, 0), self.cells.length - 1);
          if (index !== self._selectedIndex()) {
            self._syncSelection(index);
          }
        });
      });
    }

    // 旧 VariantSelector は親の variant:changed で画像/価格を更新していた
    if (this.parentVariants && this.parentVariants.element) {
      this.parentVariants.element.addEventListener('variant:changed', this._onParentVariantChanged.bind(this));
    }
  };

  VariantSelector.prototype.open = function () {
    if (this.isOpen || this.activator.getAttribute('aria-controls') !== this.element.id) return;
    this.element.setAttribute('aria-hidden', 'false');
    this.activator.setAttribute('aria-expanded', 'true');

    /* 旧 VariantSelector.open()（旧theme.js:2400）の
       Accessibility.trapFocus(this.element, 'variant-selector') 相当。
       呼ぶ位置も旧と同じ（aria-expanded の直後・no-scroll の前）。
       トラップ本体は kl-custom.js の window.klFocusTrap（旧 helper/Accessibility の移植）。
       注: 汎用 Popover（.OptionSelector のドロップダウン）は旧 components/Popover
       （旧theme.js:548-601）が trapFocus を一切呼んでいないため、こちらもトラップしない。 */
    if (window.klFocusTrap) window.klFocusTrap.trap(this.element, 'variant-selector');

    lockScroll();
    this.element.setAttribute('style', '');
    var overlay = getPageOverlay(this.element);
    if (overlay) {
      overlay.classList.add('is-visible');
      overlay.addEventListener('click', this._closeListener);
    }
    this.isOpen = true;

    // 旧 Flickity の initialIndex 相当: 開いた時点で選択中セルの位置へ即時スクロール
    if (this.carousel) {
      this.carousel.scrollLeft = this._selectedIndex() * this.carousel.clientWidth;
    }
  };

  VariantSelector.prototype.close = function () {
    if (!this.isOpen) return;
    this.element.setAttribute('aria-hidden', 'true');
    this.activator.setAttribute('aria-expanded', 'false');

    /* 旧 VariantSelector.close()（旧theme.js:2420）の
       removeTrapFocus(this.element, 'variant-selector') 相当。
       モーダルが上に開いた場合は openModal 側の clear() で既に無効化されているため、
       ここでの解除は tabindex を外すだけになり、前面のフォーカスを奪わない（旧と同じ） */
    if (window.klFocusTrap) window.klFocusTrap.release(this.element);

    unlockScroll();
    var overlay = getPageOverlay(this.element);
    if (overlay) {
      overlay.classList.remove('is-visible');
      overlay.removeEventListener('click', this._closeListener);
    }
    this.isOpen = false;
  };

  /* ------------------------------------------------------------------ */
  /* ProductVariants（旧 components/ProductVariants の移植）               */
  /* ------------------------------------------------------------------ */

  function ProductVariants(container, options) {
    var self = this;
    this.element = container;
    this.options = options;

    var jsonElement = this.element.querySelector('[data-product-json]');
    if (!jsonElement) return;
    var jsonData = JSON.parse(jsonElement.innerHTML);

    this.productData = jsonData['product'];
    this.variantsInventories = jsonData['inventories'] || {};
    this.masterSelector = this.element.querySelector('#product-select-' + this.productData['id']);

    this.productData['variants'].forEach(function (variant) {
      if (variant['id'] === jsonData['selected_variant_id']) {
        self.currentVariant = variant;
        self.option1 = variant['option1'];
        self.option2 = variant['option2'];
        self.option3 = variant['option3'];
      }
    });

    // KL-TODO: StoreAvailability は未移植（現行設定 show_pickup_availability=false）

    this._attachListeners();
    this._createSelectors();
  }

  ProductVariants.prototype._attachListeners = function () {
    var self = this;

    this.element.addEventListener('click', function (event) {
      var addToCartButton = event.target.closest('[data-action="add-to-cart"]');
      if (addToCartButton) {
        self._addToCart(event);
        return;
      }
      var decreaseButton = event.target.closest('[data-action="decrease-quantity"]');
      if (decreaseButton) {
        decreaseButton.nextElementSibling.value = Math.max(parseInt(decreaseButton.nextElementSibling.value) - 1, 1);
        return;
      }
      var increaseButton = event.target.closest('[data-action="increase-quantity"]');
      if (increaseButton) {
        increaseButton.previousElementSibling.value = parseInt(increaseButton.previousElementSibling.value) + 1;
      }
    });

    this.element.addEventListener('change', function (event) {
      if (event.target.matches('[name="quantity"]')) {
        event.target.value = Math.max(parseInt(event.target.value) || 1, 1);
        return;
      }
      if (event.target.matches('.ProductForm__Option [type="radio"]')) {
        self._onOptionChanged(null, event.target, null);
      }
    });
  };

  ProductVariants.prototype._createSelectors = function () {
    var self = this;
    this.formPopovers = [];
    this.formVariantSelectors = [];

    toArray(this.element.querySelectorAll('.OptionSelector')).forEach(function (item) {
      self.formPopovers.push(new Popover(item, {
        sectionElement: self.element,
        onValueChanged: self._onOptionChanged.bind(self)
      }));
    });

    toArray(this.element.querySelectorAll('.VariantSelector')).forEach(function (item) {
      self.formVariantSelectors.push(new VariantSelector(item, self, {
        onValueChanged: self._onOptionChanged.bind(self)
      }));
    });
  };

  ProductVariants.prototype._onVariantChanged = function (previousVariant, newVariant) {
    this._updateProductPrices(newVariant, previousVariant);
    this._updateInventory(newVariant, previousVariant);
    this._updateSku(newVariant, previousVariant);
    this._updateUnitPrice(newVariant, previousVariant);
    this._updateAddToCartButton(newVariant, previousVariant);

    this.element.dispatchEvent(new CustomEvent('variant:changed', {
      bubbles: true,
      detail: { variant: newVariant, previousVariant: previousVariant }
    }));
  };

  ProductVariants.prototype._updateProductPrices = function (newVariant, previousVariant) {
    var productMetaPrices = this.element.querySelector('.ProductMeta__PriceList');
    if (!productMetaPrices) return;

    if (!newVariant) {
      productMetaPrices.style.display = 'none';
    } else {
      if (previousVariant && previousVariant['price'] === newVariant['price'] && previousVariant['compare_at_price'] === newVariant['compare_at_price']) {
        return;
      }
      productMetaPrices.innerHTML = '';
      if (newVariant['compare_at_price'] > newVariant['price']) {
        productMetaPrices.innerHTML += '<span class="ProductMeta__Price Price Price--highlight Text--subdued u-h4" data-money-convertible>' + formatMoney(newVariant['price']) + '</span>';
        productMetaPrices.innerHTML += '<span class="ProductMeta__Price Price Price--compareAt Text--subdued u-h4" data-money-convertible>' + formatMoney(newVariant['compare_at_price']) + '</span>';
      } else {
        productMetaPrices.innerHTML += '<span class="ProductMeta__Price Price Text--subdued u-h4" data-money-convertible>' + formatMoney(newVariant['price']) + '</span>';
      }
      productMetaPrices.style.display = '';
    }
  };

  ProductVariants.prototype._updateInventory = function (newVariant) {
    if (!this.options['showInventoryQuantity']) return;

    var productFormInventory = this.element.querySelector('.ProductForm__Inventory'),
        variantInventory = newVariant ? this.variantsInventories[newVariant['id']] : null;
    if (!productFormInventory) return;

    if (!newVariant || null === variantInventory['inventory_management'] || variantInventory['inventory_quantity'] <= 0 || (this.options['inventoryQuantityThreshold'] > 0 && variantInventory['inventory_quantity'] > this.options['inventoryQuantityThreshold'])) {
      productFormInventory.style.display = 'none';
    } else {
      productFormInventory.textContent = variantInventory['inventory_message'];
      productFormInventory.style.display = '';
    }
  };

  ProductVariants.prototype._updateSku = function (newVariant) {
    if (!this.options['showSku'] || !newVariant) return;
    var productSkuNumber = this.element.querySelector('.ProductMeta__SkuNumber');
    if (productSkuNumber && newVariant['sku']) {
      productSkuNumber.innerText = newVariant['sku'];
    }
  };

  ProductVariants.prototype._updateUnitPrice = function (newVariant) {
    if (!newVariant) return;
    var unitPriceMeasurement = this.element.querySelector('.ProductMeta__UnitPriceMeasurement');
    if (!unitPriceMeasurement) return;

    if (!newVariant.hasOwnProperty('unit_price')) {
      unitPriceMeasurement.style.display = 'none';
      return;
    }

    unitPriceMeasurement.style.display = 'block';
    unitPriceMeasurement.querySelector('.UnitPriceMeasurement__Price').innerHTML = formatMoney(newVariant['unit_price']);
    unitPriceMeasurement.querySelector('.UnitPriceMeasurement__ReferenceUnit').textContent = newVariant['unit_price_measurement']['reference_unit'];

    var unitPriceReferenceValue = unitPriceMeasurement.querySelector('.UnitPriceMeasurement__ReferenceValue');
    if (unitPriceReferenceValue) {
      unitPriceReferenceValue.textContent = newVariant['unit_price_measurement']['reference_value'];
      unitPriceReferenceValue.style.display = newVariant['unit_price_measurement']['reference_value'] === 1 ? 'none' : 'inline';
    }
  };

  ProductVariants.prototype._updateAddToCartButton = function (newVariant) {
    var addToCartButton = this.element.querySelector('.ProductForm__AddToCart'),
        shopifyPaymentButton = this.element.querySelector('.shopify-payment-button');
    if (!addToCartButton) return;

    addToCartButton.classList.remove('Button--secondary');
    addToCartButton.classList.remove('Button--primary');

    if (!newVariant) {
      addToCartButton.setAttribute('disabled', 'disabled');
      addToCartButton.removeAttribute('data-action');
      addToCartButton.classList.add('Button--secondary');
      addToCartButton.innerHTML = languages().productFormUnavailable;
    } else {
      if (newVariant['available']) {
        addToCartButton.removeAttribute('disabled');
        addToCartButton.classList.add(addToCartButton.getAttribute('data-use-primary-button') === 'true' ? 'Button--primary' : 'Button--secondary');
        addToCartButton.setAttribute('data-action', 'add-to-cart');

        if (undefined === this.options['showPriceInButton'] || this.options['showPriceInButton']) {
          addToCartButton.innerHTML = '\n            <span>' + languages().productFormAddToCart + '</span>\n            <span class="Button__SeparatorDot"></span>\n            <span data-money-convertible>' + formatMoney(newVariant['price']) + '</span>\n          ';
        } else {
          addToCartButton.innerHTML = '<span>' + languages().productFormAddToCart + '</span>';
        }
      } else {
        addToCartButton.setAttribute('disabled', 'disabled');
        addToCartButton.classList.add('Button--secondary');
        addToCartButton.removeAttribute('data-action');
        addToCartButton.innerHTML = languages().productFormSoldOut;
      }
    }

    if (this.options['showPaymentButton'] && shopifyPaymentButton) {
      shopifyPaymentButton.style.display = (!newVariant || !newVariant['available']) ? 'none' : 'block';
    }
  };

  ProductVariants.prototype._onOptionChanged = function (newValue, target, activator) {
    if (activator) {
      this['option' + target.getAttribute('data-option-position')] = newValue;
      var activatorValue = activator.querySelector('.ProductForm__SelectedValue');
      if (activatorValue) activatorValue.innerHTML = newValue;
    } else {
      this['option' + target.getAttribute('data-option-position')] = target.value;
      var selectedValue = target.closest('.ProductForm__Option').querySelector('.ProductForm__SelectedValue');
      if (selectedValue) selectedValue.innerHTML = target.value;
    }

    var previousVariant = this.currentVariant;
    this.currentVariant = this._getCurrentVariantFromOptions();

    this._onVariantChanged(previousVariant, this.currentVariant);

    if (this.currentVariant) {
      if (this.options['enableHistoryState']) {
        this._updateHistoryState(this.currentVariant);
      }
      if (this.masterSelector) {
        var selectedOption = this.masterSelector.querySelector('[selected]');
        if (selectedOption) selectedOption.removeAttribute('selected');
        var newOption = this.masterSelector.querySelector('[value="' + this.currentVariant['id'] + '"]');
        if (newOption) newOption.setAttribute('selected', 'selected');
        this.masterSelector.value = this.currentVariant['id'];
      }
    }
  };

  ProductVariants.prototype._getCurrentVariantFromOptions = function () {
    var self = this,
        found = false;
    this.productData['variants'].forEach(function (variant) {
      if (variant['option1'] === self.option1 && variant['option2'] === self.option2 && variant['option3'] === self.option3) {
        found = variant;
      }
    });
    return found || null;
  };

  ProductVariants.prototype._updateHistoryState = function (variant) {
    if (!history.replaceState) return;
    var newUrl = window.location.protocol + '//' + window.location.host + window.location.pathname + '?variant=' + variant.id;
    window.history.replaceState({ path: newUrl }, '', newUrl);
  };

  ProductVariants.prototype._addToCart = function (event) {
    var self = this;

    if (!this.options['useAjaxCart']) {
      return; // cart_type=page（実測値）: 通常のフォーム送信で /cart へ遷移する（旧挙動どおり）
    }

    event.preventDefault();

    var addToCartButton = this.element.querySelector('.ProductForm__AddToCart');
    addToCartButton.setAttribute('disabled', 'disabled');

    var formElement = this.element.querySelector('form[action*="/cart/add"]');
    var cartAddUrl = (window.klProduct && window.klProduct.cartAddUrl) || '/cart/add';

    fetch(cartAddUrl + '.js', {
      body: new FormData(formElement),
      credentials: 'same-origin',
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (response) {
      if (response.ok) {
        addToCartButton.removeAttribute('disabled');
        // KL-TODO: 旧テーマではこのイベントでミニカートが再描画された。v11カートドロワー連携は未実装
        self.element.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: {
            variant: self.currentVariant,
            quantity: parseInt(formElement.querySelector('[name="quantity"]').value)
          }
        }));
      } else {
        response.json().then(function (content) {
          var errorMessageElement = document.createElement('span');
          errorMessageElement.className = 'ProductForm__Error Alert Alert--error';
          errorMessageElement.innerHTML = content['description'];
          addToCartButton.removeAttribute('disabled');
          addToCartButton.insertAdjacentElement('afterend', errorMessageElement);
          setTimeout(function () {
            errorMessageElement.remove();
          }, 2500);
        });
      }
    });
  };

  /* ------------------------------------------------------------------ */
  /* ProductImageZoom（旧 components/ProductImageZoom の完全移植。PhotoSwipe 4）
     旧テーマ同様、UIクラスは渡さず（第2引数 false）セクション内の .pswp
     カスタムマークアップ（prev/close/next の RoundButton）を自前で制御する。
     PhotoSwipe本体はズーム有効テンプレートのみが kl-photoswipe.min.js をdefer読込。 */
  /* ------------------------------------------------------------------ */

  function ProductImageZoom(sectionElement, productSection) {
    this.element = sectionElement;
    this.productSection = productSection;
    var legacyRoot = sectionElement.closest('.kl-legacy');
    this.photoswipeContainer = (legacyRoot || document).querySelector('.pswp');
    if (!this.photoswipeContainer) return;
    this._attachListeners();
  }

  ProductImageZoom.prototype._attachListeners = function () {
    var self = this;

    // 旧: delegateElement.on('click', '[data-action="open-product-zoom"]' / '.Product__SlideItem--image')
    this.element.addEventListener('click', function (event) {
      var zoomButton = event.target.closest('[data-action="open-product-zoom"]');
      if (zoomButton) {
        self._initPhotoSwipe();
        event.preventDefault();
        return;
      }
      var slideItem = event.target.closest('.Product__SlideItem--image');
      if (slideItem) {
        self._initPhotoSwipeFromImageClick(event, slideItem);
      }
    });
  };

  /**
   * モバイルのズームボタンから開く（旧 _initPhotoSwipe）。
   * パフォーマンスのため PhotoSwipe は要求時にのみ初期化し、閉じると完全に破棄される（旧同様）
   */
  ProductImageZoom.prototype._initPhotoSwipe = function () {
    var images = [];
    var cells = this.productSection.cells || [];

    cells.forEach(function (cell) {
      if (cell.classList.contains('Product__SlideItem--image')) {
        images.push(cell.querySelector('img'));
      }
    });

    var selectedElement = cells[this.productSection.selectedIndex];
    if (!selectedElement) return;

    this._createPhotoSwipeInstance(this._createPhotoSwipeItemsFromImages(images), parseInt(selectedElement.getAttribute('data-image-media-position')));
  };

  /**
   * デスクトップは専用アイコンではなく画像クリックでズームを開く（旧 _initPhotoSwipeFromImageClick）
   */
  ProductImageZoom.prototype._initPhotoSwipeFromImageClick = function (event, target) {
    // この開き方はデスクトップのみ（旧: matchesBreakpoint('pocket') なら return）
    if (matchesBreakpoint('pocket')) {
      return;
    }

    var images = toArray(this.element.querySelectorAll('.Product__SlideItem--image img'));

    this._createPhotoSwipeInstance(this._createPhotoSwipeItemsFromImages(images), parseInt(target.getAttribute('data-image-media-position')));
  };

  /**
   * 画像リストから PhotoSwipe の items 配列を生成（旧 _createPhotoSwipeItemsFromImages の完全移植）
   */
  ProductImageZoom.prototype._createPhotoSwipeItemsFromImages = function (images) {
    return images.map(function (image) {
      var maxWidth = parseInt(image.getAttribute('data-max-width')),
          maxHeight = parseInt(image.getAttribute('data-max-height')),
          maxDimension = matchesBreakpoint('phone') ? 1200 : 1800,
          // 1200 is max size for mobile and 1800 for larger devices
      reduceFactor = 1.0;

      if (maxWidth >= maxHeight) {
        reduceFactor = Math.max(maxWidth / maxDimension, 1.0);
      } else {
        reduceFactor = Math.max(maxHeight / maxDimension, 1.0);
      }

      var requestedWidth = Math.floor(maxWidth / reduceFactor);
      var requestedHeight = Math.floor(maxHeight / reduceFactor);

      return {
        msrc: image.currentSrc || image.src, // For browser that supports srcset, currentSrc is the currently used image
        w: requestedWidth,
        h: requestedHeight,
        initialZoomLevel: 0.65,
        src: getSizedImageUrl(image.getAttribute('data-original-src'), requestedWidth + 'x' + requestedHeight)
      };
    });
  };

  /**
   * PhotoSwipe インスタンス生成（旧 _createPhotoSwipeInstance の完全移植）
   */
  ProductImageZoom.prototype._createPhotoSwipeInstance = function (items, selectedImageIndex) {
    var self = this;

    if (!window.PhotoSwipe || !this.photoswipeContainer) return;

    this.photoSwipeInstance = new window.PhotoSwipe(this.photoswipeContainer, false, items, {
      index: selectedImageIndex,
      showHideOpacity: true,
      showAnimationDuration: 500,
      loop: false,
      history: false,
      closeOnVerticalDrag: false,
      allowPanToNext: false,
      pinchToClose: false,
      errorMsg: '<p class="pswp__error-msg">' + (languages().productImageLoadingError || '') + '</p>',
      scaleMode: 'zoom',
      getDoubleTapZoom: function getDoubleTapZoom(isMouseClick, item) {
        if (isMouseClick) {
          return 1.6;
        } else {
          return item.initialZoomLevel < 0.7 ? 1 : 1.33;
        }
      },
      getThumbBoundsFn: function getThumbBoundsFn(index) {
        var thumbnail = self.element.querySelector('.Product__Slideshow .Carousel__Cell[data-image-media-position="' + parseInt(index) + '"] img'),
            pageYScroll = window.pageYOffset || document.documentElement.scrollTop,
            rect = thumbnail.getBoundingClientRect();

        return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
      }
    });

    this.photoSwipeInstance.listen('beforeChange', this._onSlideChanged.bind(this));
    this.photoSwipeInstance.listen('destroy', this._destroyPhotoSwipe.bind(this));
    this.photoSwipeInstance.listen('doubleTap', this._onDoubleTap.bind(this));
    this.photoSwipeInstance.listen('initialZoomIn', this._onInitialZoomIn.bind(this));
    this.photoSwipeInstance.listen('initialZoomOut', this._onInitialZoomOut.bind(this));

    // 旧: delegateRoot.on('pswpTap', ...) — PhotoSwipeが発火するpswpTapイベントをbodyで委譲
    this._pswpTapListener = function (event) {
      if (!event.target || typeof event.target.closest !== 'function') return;
      // 登録順は旧と同じ: scroll-wrap（シングルタップ処理）→ 各ボタン
      if (event.target.closest('.pswp__scroll-wrap')) {
        self._onSingleTap(event);
      }
      if (!self.photoSwipeInstance) return; // singleTap中にcloseされた場合
      if (event.target.closest('.pswp__button--close')) {
        self.photoSwipeInstance.close();
      }
      if (event.target.closest('.pswp__button--prev')) {
        self.photoSwipeInstance.prev();
      }
      if (event.target.closest('.pswp__button--next')) {
        self.photoSwipeInstance.next();
      }
    };
    document.body.addEventListener('pswpTap', this._pswpTapListener);

    this.photoSwipeInstance.init();
  };

  /**
   * ナビの更新（旧 _onSlideChanged: 端で prev/next ボタンを disabled に）
   */
  ProductImageZoom.prototype._onSlideChanged = function () {
    if (this.photoSwipeInstance.getCurrentIndex() === 0) {
      this.photoSwipeInstance.scrollWrap.querySelector('.pswp__button--prev').setAttribute('disabled', 'disabled');
    } else {
      this.photoSwipeInstance.scrollWrap.querySelector('.pswp__button--prev').removeAttribute('disabled');
    }

    if (this.photoSwipeInstance.getCurrentIndex() + 1 === this.photoSwipeInstance.options.getNumItemsFn()) {
      this.photoSwipeInstance.scrollWrap.querySelector('.pswp__button--next').setAttribute('disabled', 'disabled');
    } else {
      this.photoSwipeInstance.scrollWrap.querySelector('.pswp__button--next').removeAttribute('disabled');
    }
  };

  /**
   * シングルタップ（旧 _onSingleTap）: デスクトップは画像クリックでズームin/out、
   * タッチではUIの表示/非表示を切替
   */
  ProductImageZoom.prototype._onSingleTap = function (event) {
    if (!event.detail || event.detail.pointerType === 'mouse') {
      if (event.target.classList.contains('pswp__img')) {
        this.photoSwipeInstance.toggleDesktopZoom(event.detail && event.detail.releasePoint);
      }
    } else {
      if (event.target.classList.contains('pswp__button')) {
        return;
      }

      var pswpRoot = event.target.closest('.pswp');
      if (pswpRoot) {
        pswpRoot.querySelector('.pswp__ui').classList.toggle('pswp__ui--hidden');
      }
    }
  };

  ProductImageZoom.prototype._onDoubleTap = function (point) {
    var initialZoomLevel = this.photoSwipeInstance.currItem.initialZoomLevel;

    if (this.photoSwipeInstance.getZoomLevel() !== initialZoomLevel) {
      this.photoSwipeInstance.zoomTo(initialZoomLevel, point, 333);
    } else {
      this.photoSwipeInstance.zoomTo(initialZoomLevel < 0.7 ? 1 : 1.33, point, 333);
    }
  };

  ProductImageZoom.prototype._onInitialZoomIn = function () {
    this.photoswipeContainer.querySelector('.pswp__ui').classList.remove('pswp__ui--hidden');
  };

  ProductImageZoom.prototype._onInitialZoomOut = function () {
    this.photoswipeContainer.querySelector('.pswp__ui').classList.add('pswp__ui--hidden');
  };

  /**
   * PhotoSwipe はクローズ時に自動破棄される（旧同様）。委譲リスナーと参照を掃除する
   */
  ProductImageZoom.prototype._destroyPhotoSwipe = function () {
    if (this._pswpTapListener) {
      document.body.removeEventListener('pswpTap', this._pswpTapListener);
      this._pswpTapListener = null;
    }
    this.photoSwipeInstance = null;
  };

  /* ------------------------------------------------------------------ */
  /* ProductSection（ギャラリー + レイアウト。旧 sections/ProductSection 移植） */
  /* ------------------------------------------------------------------ */

  function ProductSection(container) {
    var self = this;

    this.element = container;
    this.options = JSON.parse(this.element.getAttribute('data-section-settings'));
    this.viewInSpaceElement = this.element.querySelector('[data-shopify-xr]');

    if (this.options['templateSuffix'] !== 'coming-soon') {
      this.productVariants = new ProductVariants(container, this.options);
    }

    var productSlideshowElement = this.element.querySelector('.Product__Slideshow');

    if (productSlideshowElement) {
      this.slideshow = productSlideshowElement;
      this.cells = toArray(productSlideshowElement.querySelectorAll('.Carousel__Cell'));
      this.selectedIndex = 0;
      this.previouslySelectedMedia = null; // 旧theme.js:6012（選択中/再生中のメディアセルを記録）
      this.cells.forEach(function (cell, index) {
        if (cell.classList.contains('is-selected')) self.selectedIndex = index;
      });

      if (this.options['stackProductImages']) {
        this.slideshowNavDots = this.element.querySelector('.Product__SlideshowNav--dots');
        this.slideshowNavDotsItems = this.slideshowNavDots ? toArray(this.slideshowNavDots.querySelectorAll('a')) : [];
      }

      if (this.options['showThumbnails']) {
        this.slideshowNavThumbnails = this.element.querySelector('.Product__SlideshowNav--thumbnails');
        this.slideshowNavThumbnailsItems = this.slideshowNavThumbnails ? toArray(this.slideshowNavThumbnails.querySelectorAll('.Product__SlideshowNavImage')) : [];
      }

      this._setupCarousel();
      this._setupSlideshowMobileNav();
      this._setupScrollSpy();
    }

    this.productWrapperElement = this.element.querySelector('.Product__Wrapper');
    this.productInfoElement = this.element.querySelector('.Product__Info');
    this.productAsideElement = this.element.querySelector('.Product__Aside');
    this.productGalleryElement = this.element.querySelector('.Product__Gallery');
    this.quickNav = this.element.querySelector('.Product__QuickNav');

    if (this.options['enableImageZoom']) {
      this.imageZoomInstance = new ProductImageZoom(this.element, this);
    }

    // 旧テーマ同様、オフスクリーン要素をセクション末尾へ移動（コンテンツの下に表示されるのを防ぐ）
    var offscreenElement = this.element.querySelector('.Product__OffScreen');
    if (offscreenElement) {
      this.element.appendChild(offscreenElement);
    }

    this._setupDeviceFeatures();
    this._attachListeners();
  }

  /* このビューでカルーセル（横スワイプ）が有効か。
     旧Flickityの watchCSS 相当: stack設定時は1008px未満のみ、非stack時は常時 */
  ProductSection.prototype._isCarouselActive = function () {
    if (!this.options['stackProductImages']) return true;
    return !isLapAndUp();
  };

  ProductSection.prototype._setupCarousel = function () {
    var self = this;

    // 初期スライド（選択中バリアントの画像。旧Flickityの initialIndex 相当）へ即時スクロール
    if (this.selectedIndex > 0 && this._isCarouselActive()) {
      this.slideshow.scrollLeft = this.selectedIndex * this.slideshow.clientWidth;
    }

    // スクロール位置からアクティブスライドを検出（scroll-snap トラック用）
    var ticking = false;
    this.slideshow.addEventListener('scroll', function () {
      if (!self._isCarouselActive() || ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        var index = Math.round(self.slideshow.scrollLeft / Math.max(self.slideshow.clientWidth, 1));
        index = Math.min(Math.max(index, 0), self.cells.length - 1);
        if (index !== self.selectedIndex) {
          self._onImageChanged(index);
        }
      });
    });
  };

  ProductSection.prototype.selectCell = function (index) {
    if (!this.cells[index]) return;
    // 各セルは幅100%のため index * トラック幅 がスナップ位置
    this.slideshow.scrollTo({ left: index * this.slideshow.clientWidth, behavior: 'smooth' });
    this._onImageChanged(index);
  };

  /* 旧 ProductVideo.hasBeenDeselected 相当: 離脱セルのメディアを停止する。
     native video は pause()、外部動画(YouTube/Vimeo iframe)は postMessage で停止
     （旧テーマは YT.Player / Plyr 経由。プレイヤーAPIの読込は省き同じ結果を得る） */
  ProductSection.prototype._deselectMedia = function (cell) {
    if (!cell) return;

    switch (cell.getAttribute('data-media-type')) {
      case 'video':
        toArray(cell.querySelectorAll('video')).forEach(function (video) {
          video.pause();
        });
        break;

      case 'external_video':
        var iframe = cell.querySelector('iframe');
        if (iframe && iframe.contentWindow) {
          if (cell.getAttribute('data-video-host') === 'youtube') {
            iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          } else if (cell.getAttribute('data-video-host') === 'vimeo') {
            iframe.contentWindow.postMessage('{"method":"pause"}', '*');
          }
        }
        break;

      /* 旧 ProductModel.hasBeenDeselected（旧theme.js:2241-2245）= modelUi.pause()。
         旧はセル切替時（_handleMedia 旧6239）と他メディア再生時（_onMediaPlayed 旧6204）に呼ばれていた。
         移植版の3Dモデルは v11 の <model-media>（assets/theme.js の ModelMedia/BaseMedia）が
         プレイヤーを保持しているので、その pause() を使う。
         BaseMedia.pause() は playing 属性が無ければ何もしないため未ロードでも安全。 */
      case 'model':
        var modelMedia = cell.querySelector('model-media');
        if (modelMedia && typeof modelMedia.pause === 'function') {
          modelMedia.pause();
        }
        break;
    }
  };

  ProductSection.prototype._onImageChanged = function (index) {
    var self = this,
        cell = this.cells[index];

    // 直前のスライドのメディアは停止（旧 _handleMedia → hasBeenDeselected 相当）
    var previousCell = this.cells[this.selectedIndex];
    if (previousCell && previousCell !== cell) {
      this._deselectMedia(previousCell);
    }

    this.selectedIndex = index;
    /* 旧 _handleMedia の最後（旧theme.js:6263）と同じく、選択中のメディアを記録しておく。
       _onMediaPlayed（旧6203-6205）が「別のメディアが再生中なら止める」判定に使う */
    this.previouslySelectedMedia = cell;

    this.cells.forEach(function (item, i) {
      item.classList.toggle('is-selected', i === index);
    });

    // 画像以外のスライドではアクションリスト（ズーム/シェア）を隠す（モバイル）
    if (!isLapAndUp()) {
      var productGalleryActionListElement = this.element.querySelector('.Product__Gallery .Product__ActionList');
      if (productGalleryActionListElement) {
        productGalleryActionListElement.classList.toggle('is-hidden', !cell.classList.contains('Product__SlideItem--image'));
      }
    }

    // 非stack + サムネイル時はサムネイル連動
    if (!this.options['stackProductImages'] && this.options['showThumbnails'] && this.slideshowNavThumbnailsItems) {
      var mediaId = cell.getAttribute('data-media-id');
      this.slideshowNavThumbnailsItems.forEach(function (thumbnail) {
        thumbnail.classList.toggle('is-selected', thumbnail.getAttribute('data-media-id') === mediaId);
      });
    }

    // モバイルナビのドット連動
    if (this.slideshowMobileNav) {
      toArray(this.slideshowMobileNav.querySelectorAll('.dot')).forEach(function (item, i) {
        item.classList.toggle('is-selected', i === index);
      });
    }

    // 3Dモデルなら「実物大で見る」ボタンのターゲットを切替
    if (this.viewInSpaceElement) {
      if (cell.getAttribute('data-media-type') === 'model') {
        this.viewInSpaceElement.setAttribute('data-shopify-model3d-id', cell.getAttribute('data-media-id'));
      } else {
        this.viewInSpaceElement.setAttribute('data-shopify-model3d-id', this.viewInSpaceElement.getAttribute('data-shopify-model3d-default-id'));
      }
    }
  };

  /* 旧 _onMediaPlayed/_onMediaPaused（旧theme.js:6196-6224）が操作していた Flickity の
     draggable（options.draggable + unbindDrag/bindDrag）に相当する処理。
     移植版のカルーセルは Flickity ではなくネイティブ横スクロール（overflow-x:auto + scroll-snap、
     各 kl-product-*.liquid の <style> 参照）なので、ユーザー操作によるスクロールだけを止める:
       touch-action: pan-y … 横スワイプ（ドラッグ）を無効化。縦のページスクロールは維持
       overflow-x: hidden  … ホイール/トラックパッドの横スクロールを無効化
     どちらもプログラムからの scrollTo/scrollLeft には影響しないため、
     バリアント変更やサムネイルクリックによるスライド切替は従来どおり動く。 */
  ProductSection.prototype._setCarouselDraggable = function (draggable) {
    if (!this.slideshow) return;

    if (draggable) {
      this.slideshow.style.touchAction = '';
      this.slideshow.style.overflowX = '';
      return;
    }

    /* 旧もカルーセルが無効なビュー（stack表示のデスクトップ = Flickity watchCSS でデアクティブ）では
       unbindDrag が実質no-opだったので、こちらも同じ条件で何もしない
       （縦積みトラックに overflow を付けると余計なスクロールコンテナができてしまう） */
    if (!this._isCarouselActive()) return;

    this.slideshow.style.touchAction = 'pan-y';
    this.slideshow.style.overflowX = 'hidden';
  };

  /* 旧 ProductSection._onMediaPlayed（旧theme.js:6196-6212）の移植。
     旧はセル要素から bubbles:true で発火する model:played / video:played を購読していた
     （ProductModel 旧2226、ProductVideo 旧2657・2682）ため event.target はセル要素。 */
  ProductSection.prototype._onMediaPlayed = function (event) {
    // 旧6199-6200: 再生中はカルーセルのドラッグを無効化する
    this._setCarouselDraggable(false);

    var target = event.target,
        cell = target && target.closest ? target.closest('.Carousel__Cell') : null;

    // 旧6203-6205: 直前のメディアが別物なら停止する（旧は mediaList[...].hasBeenDeselected()）
    if (this.previouslySelectedMedia && this.previouslySelectedMedia !== cell) {
      this._deselectMedia(this.previouslySelectedMedia);
    }

    /* 旧6209-6211: stack表示ではカルーセルの select が呼ばれない（＝_onImageChanged が動かない）ので、
       ここで選択中メディアを記録する */
    if (this.options['stackProductImages']) {
      this.previouslySelectedMedia = cell;
    }
  };

  /* 旧 ProductSection._onMediaPaused（旧theme.js:6219-6224）の移植。
     旧は draggable = !matchesBreakpoint('supports-hover') に戻して bindDrag() していた。
     旧Flickityはホバー可能デバイス（デスクトップ）では初期状態から draggable:false だった
     （旧theme.js:6010 の Carousel 生成オプション）ため、旧では停止後もデスクトップはドラッグ不可のまま。
     移植版のカルーセルはネイティブ横スクロールで初期状態からスクロール可能なので、ここは
     「再生前の状態へ戻す」＝インラインの無効化を外す、とする。
     現行設定（stack_images=true）ではカルーセルが存在するのは 1008px 未満＝タッチ端末のみで、
     そこでは旧の式も true（ドラッグ可）になるため実機での結果は旧と一致する。 */
  ProductSection.prototype._onMediaPaused = function () {
    this._setCarouselDraggable(true);
  };

  ProductSection.prototype._setupSlideshowMobileNav = function () {
    var self = this;
    this.slideshowMobileNav = this.element.querySelector('.Product__SlideshowMobileNav');
    if (!this.slideshowMobileNav) return;

    this.slideshowMobileNav.addEventListener('click', function (event) {
      var dot = event.target.closest('.dot');
      if (dot) {
        self.selectCell(parseInt(dot.getAttribute('data-index')));
        return;
      }
      var arrow = event.target.closest('.Product__SlideshowNavArrow');
      if (arrow) {
        if (arrow.getAttribute('data-direction') === 'next') {
          self.selectCell(Math.min(self.selectedIndex + 1, self.cells.length - 1));
        } else {
          self.selectCell(Math.max(self.selectedIndex - 1, 0));
        }
      }
    });
  };

  /* デスクトップのstack表示時、スクロール位置に応じてドット/サムネイルを同期
     （旧 ScrollSpy の IntersectionObserver 版） */
  ProductSection.prototype._setupScrollSpy = function () {
    var self = this;
    if (!this.options['stackProductImages'] || this.cells.length < 2 || !('IntersectionObserver' in window)) return;

    this.scrollSpyObserver = new IntersectionObserver(function (entries) {
      if (!isLapAndUp()) return;
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var index = self.cells.indexOf(entry.target);
        if (index === -1) return;

        if (self.slideshowNavDotsItems) {
          self.slideshowNavDotsItems.forEach(function (item, i) {
            item.classList.toggle('is-selected', i === index);
          });
        }
        if (self.options['showThumbnails'] && self.slideshowNavThumbnailsItems) {
          self.slideshowNavThumbnailsItems.forEach(function (item, i) {
            item.classList.toggle('is-selected', i === index);
          });
        }
      });
    }, { rootMargin: '-250px 0px -50% 0px' });

    this.cells.forEach(function (cell) {
      self.scrollSpyObserver.observe(cell);
    });
  };

  ProductSection.prototype._attachListeners = function () {
    var self = this;

    this.element.addEventListener('click', function (event) {
      var shareToggle = event.target.closest('[data-action="toggle-social-share"]');
      if (shareToggle) {
        shareToggle.classList.toggle('is-active');
        shareToggle.classList.toggle('RoundButton--secondaryState');
        shareToggle.setAttribute('aria-expanded', shareToggle.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
        shareToggle.nextElementSibling.setAttribute('aria-hidden', shareToggle.nextElementSibling.getAttribute('aria-hidden') === 'true' ? 'false' : 'true');
        return;
      }

      // 非stack時のサムネイルクリック（stack時はアンカーで kl-custom.js のスムーススクロールが処理）
      if (!self.options['stackProductImages'] && self.options['showThumbnails']) {
        var thumbnail = event.target.closest('.Product__SlideshowNavImage');
        if (thumbnail) {
          event.preventDefault();
          for (var i = 0; i < self.cells.length; i++) {
            if (self.cells[i].getAttribute('data-media-id') === thumbnail.getAttribute('data-media-id')) {
              self.selectCell(i);
              break;
            }
          }
        }
      }
    });

    // バリアント変更→ギャラリー画像切替
    this.element.addEventListener('variant:changed', function (event) {
      self._updateSlideshowImage(event);
    });

    /* 旧 _attachListeners（旧theme.js:6114-6118）のメディア再生・停止購読の移植。
       旧イベント名（セル要素から bubbles:true で発火）をそのまま購読する。 */
    var mediaPlayedListener = function (event) { self._onMediaPlayed(event); };
    var mediaPausedListener = function () { self._onMediaPaused(); };

    this.element.addEventListener('model:played', mediaPlayedListener);
    this.element.addEventListener('video:played', mediaPlayedListener);
    this.element.addEventListener('model:paused', mediaPausedListener);
    this.element.addEventListener('video:paused', mediaPausedListener);

    /* 3Dモデルの再生・停止は、移植版では v11 の <model-media> カスタム要素
       （assets/theme.js の ModelMedia / BaseMedia）が担当している。
       kl-product-*.liquid の model スライドは model_viewer_tag を <model-media> で包んでおり、
       BaseMedia が playing 属性の変化で media:play / media:pause を bubbles:true で発火する
       （assets/theme.js の attributeChangedCallback）。旧 ProductModel の
       model:played / model:paused と同じタイミングなのでここに接続する
       （旧 ProductModel 自体は未移植。KL-TODO 参照）。 */
    this.element.addEventListener('media:play', mediaPlayedListener);
    this.element.addEventListener('media:pause', mediaPausedListener);

    /* 旧 ProductVideo は Plyr の play/pause で video:played / video:paused を発火していた
       （旧theme.js:2656-2661）。移植版の動画スライドは素の <video controls> なので、
       同じ結果になるようネイティブの play/pause を拾う（これらは bubble しないので capture で購読）。
       外部動画(YouTube)は YT.Player 未移植のため対象外（旧2681-2684 相当は未移植）。
       旧の ProductVideo はギャラリー内のメディアだけを対象にしていたので、
       セクション内でもギャラリー外の動画（モーダル内の動画など）は対象外にする。 */
    var isGalleryVideo = function (event) {
      return self.slideshow && event.target && event.target.tagName === 'VIDEO' && self.slideshow.contains(event.target);
    };

    this.element.addEventListener('play', function (event) {
      if (isGalleryVideo(event)) self._onMediaPlayed(event);
    }, true);
    this.element.addEventListener('pause', function (event) {
      if (isGalleryVideo(event)) self._onMediaPaused();
    }, true);

    if (this.quickNav) {
      window.addEventListener('scroll', function () {
        var showAsideQuickNav = window.scrollY >= self.productAsideElement.offsetTop - self.productAsideElement.clientHeight;
        self.quickNav.classList.toggle('is-flipped', showAsideQuickNav);
      });
    }

    // ブレークポイント変化でレイアウト計算をやり直し
    var lapMedia = window.matchMedia('(min-width: ' + LAP_MIN + 'px)');
    var mediaListener = function () {
      self._setupDeviceFeatures();
    };
    if (lapMedia.addEventListener) {
      lapMedia.addEventListener('change', mediaListener);
    } else if (lapMedia.addListener) {
      lapMedia.addListener(mediaListener);
    }
  };

  ProductSection.prototype._updateSlideshowImage = function (event) {
    if (!this.slideshow) return;

    var variant = event.detail.variant,
        previousVariant = event.detail.previousVariant;

    if (!variant || !variant['featured_media'] || (previousVariant && previousVariant['featured_media'] && previousVariant['featured_media']['id'] === variant['featured_media']['id'])) {
      return;
    }

    if (this._isCarouselActive()) {
      for (var i = 0; i < this.cells.length; i++) {
        if (parseInt(this.cells[i].getAttribute('data-media-id')) === variant['featured_media']['id']) {
          this.selectCell(i);
          break;
        }
      }
    } else {
      // stackデスクトップ: 旧実装と同じくサムネイル/ドットのアンカーをクリック（kl-custom.jsがスムーススクロール）
      var anchor = document.querySelector('[href="#Media' + variant['featured_media']['id'] + '"]');
      if (anchor) anchor.click();
    }
  };

  /* 旧 _setupDeviceFeatures のうちレイアウト計算部分の移植
     （OverflowScroller と Stickyfill は未移植: sticky はネイティブCSS対応） */
  ProductSection.prototype._setupDeviceFeatures = function () {
    var self = this;

    if (!isLapAndUp()) {
      if (this.productAsideElement) {
        this.productAsideElement.style.minHeight = null;
      } else if (this.productWrapperElement) {
        this.productWrapperElement.style.minHeight = null;
      }
      if (this.productInfoElement && this.productInfoElement.parentNode) {
        this.productInfoElement.parentNode.style.maxHeight = null;
      }
      return;
    }

    if (!this.productInfoElement) return;

    var productInfoStyles = window.getComputedStyle(this.productInfoElement),
        productInfoPadding = parseInt(productInfoStyles.paddingTop) + parseInt(productInfoStyles.paddingBottom),
        productGalleryHeight = this.productGalleryElement ? parseInt(this.productGalleryElement.scrollHeight) : 0;

    var calculateMinHeight = function () {
      if (self.productAsideElement) {
        self.productAsideElement.style.minHeight = (self.productInfoElement.scrollHeight - productInfoPadding - productGalleryHeight) + 'px';
        var infoWrapper = self.productInfoElement.closest('.Product__InfoWrapper');
        if (infoWrapper) {
          infoWrapper.style.maxHeight = (self.productAsideElement.offsetTop + self.productInfoElement.scrollHeight - productInfoPadding) + 'px';
        }
      } else if (self.productWrapperElement) {
        self.productWrapperElement.style.minHeight = (self.productInfoElement.scrollHeight - parseInt(productInfoStyles.paddingTop)) + 'px';
      }
    };

    calculateMinHeight();

    if (window.ResizeObserver && !this.productInfoResizeObserver) {
      this.productInfoResizeObserver = new ResizeObserver(function () {
        if (isLapAndUp()) calculateMinHeight();
      });
      this.productInfoResizeObserver.observe(this.productInfoElement);
    }
  };

  /* ------------------------------------------------------------------ */
  /* init                                                                */
  /* ------------------------------------------------------------------ */

  function initProductSections(root) {
    toArray((root || document).querySelectorAll('.kl-legacy [data-section-type="product"]')).forEach(function (sectionElement) {
      if (sectionElement.klProductInitialized) return;
      sectionElement.klProductInitialized = true;
      new ProductSection(sectionElement);
    });
  }

  function init() {
    bindGlobalHandlers();
    initProductSections(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // テーマエディタ対応
  document.addEventListener('shopify:section:load', function (event) {
    initProductSections(event.target);
  });
})();
